import { cn } from '@/lib/utils'

interface PasswordStrengthIndicatorProps {
  password: string
}

function calculatePasswordStrength(password: string): number {
  let strength = 0

  if (!password) return 0

  // Length check
  if (password.length >= 8) strength++

  // Mixed case check
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++

  // Number check
  if (/[0-9]/.test(password)) strength++

  // Special character check
  if (/[^A-Za-z0-9]/.test(password)) strength++

  return strength
}

function getStrengthLabel(strength: number): string {
  switch (strength) {
    case 0:
      return 'Très faible'
    case 1:
      return 'Faible'
    case 2:
      return 'Moyen'
    case 3:
      return 'Bon'
    case 4:
      return 'Fort'
    default:
      return 'Très faible'
  }
}

function getStrengthColor(strength: number): string {
  switch (strength) {
    case 0:
    case 1:
      return 'bg-red-500'
    case 2:
    case 3:
      // Green is reserved for level 4: every listed requirement met. At level 3 one is
      // still missing and the form says so right below the bar.
      //
      // amber-600, not yellow-500. A filled segment must read against the UNFILLED ones, which
      // are gray-200: yellow-500 sat at 1.55:1 there and green-500 at 2.60, both under the 3:1
      // WCAG 1.4.11 asks of a graphical object. amber-600 gives 3.28 and green-700 4.53.
      return 'bg-amber-600'
    case 4:
      return 'bg-green-700'
    default:
      return 'bg-gray-200'
  }
}

export function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const strength = calculatePasswordStrength(password)
  const label = getStrengthLabel(strength)
  const color = getStrengthColor(strength)

  if (!password) {
    return null
  }

  return (
    <div className="mt-2">
      {/* Strength bars */}
      <div className="flex gap-1">
        {[0, 1, 2, 3].map((index) => (
          <div
            key={index}
            className={cn(
              'h-1 flex-1 rounded transition-colors duration-300',
              index < strength ? color : 'bg-gray-200'
            )}
          />
        ))}
      </div>

      {/* Strength label */}
      <p
        className={cn(
          'text-xs mt-1 font-medium transition-colors duration-300',
          strength <= 1 && 'text-red-600',
          // 12px text on a public page, so every one of these needs AA 4.5:1 on white. Measured:
          // red-600 4.83 ✓ · amber-700 5.05 ✓ · green-700 4.53 ✓. The two that were here failed
          // — yellow-600 at 2.93 and green-600 at 3.22 — and an earlier pass fixed only the first
          // two levels, leaving « Fort » as the single unreadable state on the sign-up form.
          (strength === 2 || strength === 3) && 'text-amber-700',
          strength === 4 && 'text-green-700'
        )}
      >
        Force du mot de passe : {label}
      </p>

      {/* Password requirements hint */}
      {strength < 4 && (
        <div className="mt-2 text-xs text-muted-foreground space-y-1">
          <p className="font-medium">Requis :</p>
          <ul className="list-disc list-inside space-y-0.5">
            {password.length < 8 && <li>Au moins 8 caractères</li>}
            {!/[A-Z]/.test(password) && <li>Une lettre majuscule</li>}
            {!/[a-z]/.test(password) && <li>Une lettre minuscule</li>}
            {!/[0-9]/.test(password) && <li>Un chiffre</li>}
            {!/[^A-Za-z0-9]/.test(password) && <li>Un caractère spécial (!@#$%^&*...)</li>}
          </ul>
        </div>
      )}
    </div>
  )
}
